/**
 * The submit flow (HIG-5971): everything that happens between "the user hit
 * send" and the turn settling — input reconciliation, the queue decision,
 * optimistic commit, attachment finalization, and the engine handoff.
 * Dependency-injected so `useSubmitMessage` is a thin React facade and the
 * flow is testable with literal deps, mirroring the engine's services. The
 * fast-path/queue-path divergence lives entirely in `submit-presentation.ts`;
 * this flow is mode-free. The only module-level side channels are the
 * `@highlight/ipc` globals (settings read + cross-window events).
 *
 * A submission runs as named stages over one mutable `TurnDraft`:
 * `prepareTurnDraft` → presentation → `collectSubmissionContext` →
 * `resolveModelAndPlugins` → `resolveTurnAttachments` → `runEngineTurn`.
 * The draft is the turn's evolving identity (ids, input, options) — the
 * `metadata` event renames `conversationId`/`pendingMessageId` on it mid-run,
 * which is why stages share one object instead of passing values.
 */
import { v4 as uuidv4 } from 'uuid'
import type { PendingMessage } from '../store'
import type { ResetChatContextOptions } from '../store/atoms'
import {
  ChatAttachment,
  ChatMessage,
  FinalizedAttachment,
  MentionableItem,
  MentionableShortcut,
  SendMessageOptions,
  UserMessage,
} from '../types'
import type { components as BackendOpenApiComponents } from '../types/backend-openapi'
import { getModelIdForMessage } from '../utils/chat'
import { createAssistantPlaceholder } from '../utils/message-reconciliation'
import { attachmentUploadError, toChatTurnError, type ChatTurnError } from '../engine/errors'
import { reportTurnError } from '../engine/telemetry'
import type { ChatRequestBody, EngineDeps } from '../engine/services'
import type { ReasoningEffort } from './use-reasoning-effort'
import {
  attachmentErrorMessage,
  reconcileChatInput,
  resolveAttachedContext,
  type FinalizeAttachmentsResult,
} from './submit-helpers'
import { collectRequestedContextAttachments, waitForAssistantContext } from './submit-context'
import { createTurnPresentation, type SubmitQueue, type TurnPresentation } from './submit-presentation'
import { handleTurnFailure, runEngineTurn } from './submit-turn'
import { trackSubmitMessage } from './submit-telemetry'
import type { AutoToolTracker } from './use-auto-tool-responses'

export type SubmitMessage = (
  input?: string | ((prevValue: string) => string) | undefined | null,
  options?: SendMessageOptions,
) => Promise<void>

/**
 * Hook-bound state and setters the flow needs; built fresh per render by
 * `useSubmitMessage`. Grouped the way `EngineDeps` is so each stage can
 * declare which slice it touches.
 */
export type SubmitFlowDeps = {
  engine: EngineDeps
  queue: SubmitQueue
  /** Render-time snapshots (same capture semantics as the pre-engine hook). */
  snapshot: {
    activeChatId: string | undefined
    isExistingChat: boolean
    allChatMessages: Record<string, Array<ChatMessage>>
    attachments: Array<ChatAttachment>
    messageMentions: Array<MentionableItem>
    autoMemoryCollection: boolean
    /** Whether the plan lets the user pick the model; without it the turn names none and the server picks. */
    modelChoice: boolean
    /** Unset keeps the model's default reasoning depth. */
    reasoningEffort?: ReasoningEffort
  }
  // Live (non-hook) reads — the snapshots above close over the previous render
  // and would miss state set during this submission.
  live: {
    getCurrentChatInput: () => string | undefined
    getChatMessages: (chatId: string) => Array<ChatMessage> | undefined
  }
  /** Store writers. */
  store: {
    appendMessage: (chatId: string, message: ChatMessage) => void
    updateLastMessage: (chatId: string, message: Partial<ChatMessage>) => void
    removeLastMessage: (chatId: string, message: Partial<ChatMessage>) => void
    updateMessagesChatId: (oldChatId: string, newChatId: string) => void
    removeUnadoptedPlaceholder: (chatId: string, clientId: string) => void
    setPendingMessage: (
      update: PendingMessage | undefined | ((prev: PendingMessage | undefined) => PendingMessage | undefined),
    ) => void
    setChatInput: (value: string) => void
    setActiveChatId: (update: string | ((prev: string | undefined) => string | undefined)) => void
    setTurnError: (error: ChatTurnError) => void
    clearTurnError: (chatId: string) => void
    /** Records a minted temp id so nothing fetches it by id before the backend confirms it. */
    trackLocalChatId: (chatId: string) => void
    confirmServerChatId: (chatId: string) => void
    /** The id the backend prepared the next new chat under, if it confirmed one. Hands each id out once. */
    takePreparedConversationId: () => string | undefined
    /** The id the composer granted a new chat's attached files under, if any. Wins over a prepared id, once. */
    takeNewChatSandboxId: () => string | undefined
  }
  /** Mentions + UI. */
  ui: {
    validateMentions: (text: string, mentions: Array<MentionableItem>) => Array<MentionableItem> | null | undefined
    resetMentions: () => void
    scrollToEnd: () => void
    /** Hands the backend's onboarding refusal to main, which delivers an owed stamp or reopens the flow. */
    restartOnboarding: () => void
  }
  /** Data plumbing. */
  data: {
    refreshDefaultModel: () => Promise<string | null | undefined>
    invalidateHistoryPreview: () => void
    invalidateChatHistory: () => void
    reconcileChatMessages: (chatId: string) => Promise<void>
    addChatIdMapping: (tempId: string, realId: string) => void
    finalizeAttachments: (attachments: Array<ChatAttachment>, chatId: string) => Promise<FinalizeAttachmentsResult>
    contextToAttachments: () => Array<ChatAttachment>
    resetChatContext: (options?: ResetChatContextOptions) => void
  }
  /** Actions provider + auto tool responses. */
  actions: {
    onChunkParsed?: (
      event: BackendOpenApiComponents['schemas']['ChatSSEStreamEvent'],
      options?: SendMessageOptions,
    ) => Promise<void> | void
    requestCurrentContext?: Parameters<typeof collectRequestedContextAttachments>[0]
    getActiveDisplayScreenshot?: Parameters<typeof collectRequestedContextAttachments>[2]
    createAutoToolTracker: (params: {
      getConversationId: () => string
      startFollowUp: () => Promise<void>
    }) => AutoToolTracker
  }
}

// Bundles the optimistic transcript + streaming-state work that happens when a
// turn actually starts. The transcript presentation runs this at submit time;
// the queued presentation defers it until the held-open SSE emits 'metadata'.
function commitTurnStart(deps: SubmitFlowDeps, draft: TurnDraft): void {
  const { conversationId, chatInput } = draft
  // Live read — the queue path defers this commit until `metadata`, long
  // after the render-time snapshot was captured.
  const existingMessages = deps.live.getChatMessages(conversationId)
  if (existingMessages) {
    const lastUserMessage = [...existingMessages].reverse().find((m) => m.role === 'user')
    if (lastUserMessage?.error) {
      deps.store.removeLastMessage(conversationId, { role: 'user' })
    }
  }
  if (chatInput) {
    deps.store.appendMessage(conversationId, draft.initialMessage)
  }
  deps.store.appendMessage(
    conversationId,
    createAssistantPlaceholder(conversationId, draft.assistantClientId, {
      useAdvancedReasoning: draft.options?.useAdvancedReasoning,
      model_id: draft.modelId,
    }),
  )
  deps.engine.streaming.begin(conversationId, chatInput || undefined)
  // Queue-path scroll: direct submits scroll during attachment resolution, but
  // queue drains only add their user + placeholder messages here (after a
  // 'metadata' event), so the list needs its own scrollToEnd to follow.
  deps.ui.scrollToEnd()
}

/** Backend tools from the current mentions plus previous user messages in the chat. */
function collectBackendPlugins(
  mentions: Array<MentionableItem>,
  chatMessages: Array<ChatMessage> | undefined,
): Array<string> {
  const backendPlugins = [
    ...mentions.filter((mention) => mention.type === 'backend_plugin').map((plugin) => plugin.value),
    ...(chatMessages
      ?.filter((msg) => msg.role === 'user')
      .flatMap((msg) => msg.mentions || [])
      .filter((mention) => mention.type === 'backend_plugin')
      .map((mention) => mention.value) || []),
  ]
  return Array.from(new Set(backendPlugins))
}

function buildChatRequestBody(params: {
  chatInput: string | undefined
  conversationId: string | undefined
  activeShortcut: MentionableShortcut | undefined
  activeSkills: MentionableShortcut[]
  attachedContext: Array<FinalizedAttachment>
  modelId: string | undefined
  backendPlugins: Array<string>
  useMemory: boolean
  reasoningEffort: ReasoningEffort | undefined
  options: SendMessageOptions | undefined
  msgId: string
  /** `conversationId` is one this client chose for a chat the backend has not seen a message for. */
  opensNewConversation: boolean
}): ChatRequestBody {
  const { options } = params
  const skillIds = [...new Set(params.activeSkills.flatMap((skill) => skill.skillId ?? []))]
  const body: ChatRequestBody = {
    prompt: params.chatInput,
    conversationId: params.conversationId,
    msgId: params.msgId,
    shortcutId: params.activeShortcut?.value,
    skillIds,
    attachedContext: params.attachedContext,
    modelId: params.modelId,
    reasoningEffort: params.reasoningEffort,
    backendPlugins: params.backendPlugins,
    useMemory: params.useMemory,
    useKnowledge: options?.useKnowledge ?? false,
    ephemeral: options?.ephemeral ?? false,
    // TODO: Handle timezone
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone ?? undefined,
  }
  if (options?.isPushToTalk) {
    body.pushToTalkMode = (options.pushToTalkMode ?? 'ask') as 'ask' | 'scribe'
  }
  if (params.opensNewConversation) {
    // Ahead of the generated API types. Without it the gateway would look for an existing conversation.
    Object.assign(body, { newConversation: true })
  }
  if (options?.skillCompare) {
    // Ahead of the generated API types: the gateway reads these on the admin-only compare path.
    Object.assign(body, {
      skillComparePairId: options.skillCompare.pairId,
      skillCompareSide: options.skillCompare.side,
    })
  }
  return body
}

/**
 * One submission's evolving state, threaded through the stages below. Mutable
 * on purpose: the `metadata` event renames `conversationId` and
 * `pendingMessageId` mid-turn, context collection rebinds `options`, and the
 * model resolves after the presentation's commit closure was created — every
 * stage reads the draft's current values, never a stale copy.
 */
export type TurnDraft = {
  options: SendMessageOptions | undefined
  chatInput: string | undefined
  conversationId: string
  backendConversationId: string | undefined
  /**
   * The id this new chat opens under: one the caller chose, or the one the
   * backend prepared it under. `conversationId` stays this window's local id
   * until `metadata` names the conversation, the same as when the backend
   * picks the id.
   */
  preparedConversationId: string | undefined
  /** The prepared id when it is the sandbox the composer granted this chat's attached files under. */
  newChatSandboxId: string | undefined
  isExisting: boolean
  mentions: Array<MentionableItem>
  activeShortcut: MentionableShortcut | undefined
  activeSkills: MentionableShortcut[]
  msgId: string
  /**
   * Identity for this turn's optimistic assistant placeholder; the engine
   * adopts it with the first real assistant message (same position, same
   * clientId) so the group key never changes across the seam.
   */
  assistantClientId: string
  initialMessage: UserMessage
  stagedActionContext: Array<ChatAttachment>
  /**
   * Retry handle for this submission. Starts as the local user-message id and
   * follows the backend id once `metadata` renames the message, so the
   * pending-message match that gates the Retry button survives the rename.
   */
  pendingMessageId: string
  /** Resolved by `resolveModelAndPlugins`; the queued presentation's deferred commit reads it late. */
  modelId: string | undefined
  /** Composer text before the submit cleared it, for the cap-race rollback. */
  inputBeforeSubmit: string
}

/** Submission-scoped state the outer catch/finally reads after the stages ran (or threw). */
export type SubmissionState = {
  /**
   * The conversation that actually started streaming — follows the temp→real
   * rename, unlike the render-time `activeChatId` snapshot.
   */
  streamingChatId: string | undefined
  /**
   * Whether this submission still owned its registry turn when it settled. A
   * superseding turn takes over the shared per-chat cleanup, so a superseded
   * run must leave it alone.
   */
  ownedTurnAtSettle: boolean
  /** How this submission presents before it streams (transcript vs queue bubble). */
  presentation: TurnPresentation | null
}

/** Reconcile inputs, parse mentions, and mint the turn's identities. */
function prepareTurnDraft(
  deps: SubmitFlowDeps,
  chatInput: string | undefined,
  options: SendMessageOptions | undefined,
  existingConversationId: string | undefined,
): TurnDraft {
  const isExisting = !!existingConversationId && !options?.isNewChat
  const conversationId = isExisting ? existingConversationId : uuidv4()
  if (!isExisting) deps.store.trackLocalChatId(conversationId)
  // Attached files are granted under the chat's id before it exists, so that id must name the chat.
  const opensComposerChat = !isExisting && !!options?.usePreparedConversation
  const newChatSandboxId = opensComposerChat
    ? (options?.newChatSandboxId ?? deps.store.takeNewChatSandboxId())
    : undefined
  const preparedConversationId = isExisting
    ? undefined
    : (options?.newConversationId ??
      newChatSandboxId ??
      (opensComposerChat ? deps.store.takePreparedConversationId() : undefined))

  // A new turn supersedes the chat's previous failure.
  deps.store.clearTurnError(conversationId)

  // Clear chat input for immediate UI update. Capture the original first
  // so the cap-race rollback path can restore it without the user losing
  // their typed message. Programmatic submits that consume no composer
  // text must not clear (or later restore over) the user's draft.
  const inputBeforeSubmit = chatInput ?? ''
  if (!options?.preserveDraft) deps.store.setChatInput('')

  // Parse any @mentions or /shortcuts from the input text
  const mentions = deps.ui.validateMentions(chatInput ?? '', options?.mentions ?? deps.snapshot.messageMentions) || []
  const activeShortcut = mentions.find(
    (mention): mention is MentionableShortcut => mention.type === 'shortcut' && !mention.skillId,
  )
  const activeSkills = mentions.filter(
    (mention): mention is MentionableShortcut => mention.type === 'shortcut' && !!mention.skillId,
  )
  // Model mentions are consumed by model selection, not sent as prompt text.
  mentions.forEach((mention) => {
    if (mention.type === 'model') {
      chatInput = chatInput?.replace(`@${mention.label}`, '').trim()
    }
  })

  // The backend id replaces `id` on the metadata event; `clientId`
  // survives the swap so the bubble's group key never changes.
  const userMessageId = uuidv4()
  const stagedActionContext: Array<ChatAttachment> = options?.stagedActionAttachment
    ? [{ pending: options.stagedActionAttachment }]
    : []
  const initialMessage: UserMessage = {
    id: userMessageId,
    clientId: userMessageId,
    role: 'user',
    version: 'v4',
    content: chatInput,
    conversation_id: conversationId,
    mentions: mentions,
    additional_tools: [],
    ...(stagedActionContext.length > 0 ? { pending_context: stagedActionContext } : {}),
  }

  return {
    options,
    chatInput,
    conversationId,
    backendConversationId: isExisting ? conversationId : undefined,
    preparedConversationId,
    newChatSandboxId,
    isExisting,
    mentions,
    activeShortcut,
    activeSkills,
    msgId: options?.resendMsgId ?? uuidv4(),
    assistantClientId: uuidv4(),
    initialMessage,
    stagedActionContext,
    pendingMessageId: initialMessage.id,
    modelId: undefined,
    inputBeforeSubmit,
  }
}

/** Wait out in-flight context processing, then snapshot and clear the composer's attachments. */
async function collectSubmissionContext(
  deps: SubmitFlowDeps,
  draft: TurnDraft,
  turn: TurnPresentation,
): Promise<{ submittedAttachments: Array<ChatAttachment>; submittedAttachedContext: Array<ChatAttachment> }> {
  const { options } = draft

  // Check if we should wait for existing context to process
  if (options?.waitForContext !== false && !options?.requestCurrentContext && !options?.attachments) {
    await waitForAssistantContext()
  }

  // External composers provide their own context and leave the shared composer untouched.
  const submittedAttachments = options?.preserveComposerContext ? [] : deps.snapshot.attachments.slice()
  const submittedAttachedContext = options?.preserveComposerContext
    ? []
    : deps.data.contextToAttachments().concat(submittedAttachments)
  if (!options?.preserveComposerContext) {
    // Attachments the caller replaced are dropped unsent, so their local paths lose their grants.
    deps.data.resetChatContext({ keepViewScoped: true, keepLocalPathGrants: !draft.options?.attachments })
  }

  if (!options?.requestCurrentContext && !options?.attachments && draft.chatInput) {
    turn.stampUserMessage({ pending_context: [...submittedAttachedContext, ...draft.stagedActionContext] })
  }

  return { submittedAttachments, submittedAttachedContext }
}

/** Resolve the turn's model (refresh already in flight) and backend plugins. */
async function resolveModelAndPlugins(
  deps: SubmitFlowDeps,
  draft: TurnDraft,
  modelRefreshPromise: Promise<string | null | undefined>,
): Promise<Array<string>> {
  const latestDefaultModelId = await modelRefreshPromise
  draft.modelId = getModelIdForMessage({
    mentions: draft.mentions,
    isExistingChat: draft.isExisting,
    conversationId: draft.conversationId,
    allMessages: deps.snapshot.allChatMessages,
    defaultModel: latestDefaultModelId ?? undefined,
    useAdvancedReasoning: draft.options?.useAdvancedReasoning,
    modelChoice: deps.snapshot.modelChoice,
  })
  return collectBackendPlugins(draft.mentions, deps.snapshot.allChatMessages[draft.conversationId])
}

/**
 * Collect requested window context, upload pending attachments, and stamp the
 * finalized context onto the user message. Returns null after funneling the
 * failure when an upload failed — the submission is over.
 */
async function resolveTurnAttachments(
  deps: SubmitFlowDeps,
  draft: TurnDraft,
  turn: TurnPresentation,
  submittedAttachedContext: Array<ChatAttachment>,
): Promise<{
  attachedContext: Array<FinalizedAttachment>
  includedAttachments: Array<ChatAttachment>
  onTurnAccepted?: () => void
} | null> {
  // Follow-up messages in floaty chat w/ continuous context request fresh
  // window context and attach it.
  if (draft.options?.requestCurrentContext && typeof deps.actions.requestCurrentContext === 'function') {
    const collected = await collectRequestedContextAttachments(
      deps.actions.requestCurrentContext,
      draft.options,
      deps.actions.getActiveDisplayScreenshot,
    )
    // Rebind the draft's options rather than mutating the caller's object;
    // every later read (pending message, telemetry, observer) sees the
    // collected attachments.
    if (collected) draft.options = { ...draft.options, attachments: collected }
  }

  // Upload any pending attachments and finalize the attachedContext.
  const includedAttachments: Array<ChatAttachment> = draft.options?.attachments ?? submittedAttachedContext
  const { attachedContext, uploadError, onTurnAccepted } = await resolveAttachedContext({
    chatInput: draft.chatInput,
    includedAttachments,
    preFinalized: draft.options?.attachedContext,
    finalizeAttachments: deps.data.finalizeAttachments,
    chatId: draft.conversationId,
    userMessageId: draft.initialMessage.id,
  })

  if (uploadError) {
    turn.onUploadError()
    const failure = attachmentUploadError(draft.conversationId, attachmentErrorMessage(uploadError), uploadError.rayId)
    reportTurnError(failure, {
      isNewChat: !draft.isExisting,
      messageLength: draft.chatInput?.length,
      hasAttachments: true,
      messageRole: 'user',
    })
    handleTurnFailure(deps, failure, {
      chatId: draft.conversationId,
      wasTurnCommitted: turn.committed,
      userStamp: { id: draft.initialMessage.id, mentions: draft.mentions, omit: !draft.chatInput },
    })
    return null
  }
  turn.onAttachmentsFinalized(attachedContext)

  // Stamp the finalized context onto the user message. attached_context
  // now carries the finalized uploads; the only thing left in
  // pending_context is the staged-action chip if provided.
  if (draft.chatInput) {
    turn.stampUserMessage({
      attached_context: attachedContext,
      pending_context: draft.stagedActionContext.length > 0 ? draft.stagedActionContext : undefined,
      mentions: draft.mentions,
    })
  }

  // Set the pending message until it is processed
  if (!draft.options?.preservePendingMessage) {
    deps.store.setPendingMessage({
      id: draft.initialMessage.id,
      input: draft.chatInput ?? '',
      options: withRetryLocalPaths(draft, attachedContext),
    })
  }
  deps.ui.scrollToEnd()

  return { attachedContext, includedAttachments, onTurnAccepted }
}

/**
 * The options Retry resubmits. The composer is already cleared, so they carry the attached local paths and the sandbox
 * they were granted under; without them a retried chat could neither find those files nor tell the model about them.
 */
function withRetryLocalPaths(
  draft: TurnDraft,
  attachedContext: Array<FinalizedAttachment>,
): SendMessageOptions | undefined {
  const preFinalized = draft.options?.attachedContext ?? []
  const localPaths = attachedContext.filter(
    (attachment) => attachment.type === 'localPath' && !preFinalized.includes(attachment),
  )
  if (localPaths.length === 0 && !draft.newChatSandboxId) {
    return draft.options
  }
  return {
    ...draft.options,
    attachedContext: [...preFinalized, ...localPaths],
    newChatSandboxId: draft.newChatSandboxId,
  }
}

/**
 * Kick a continuation turn — resume a conversation with no new prompt so the
 * model consumes a freshly posted tool result. The ONE implementation of the
 * kick: skips when queued sends exist (the queued turns consume the result as
 * they drain — kicking would just add an empty-prompt turn behind them), and
 * preserves the composer draft since a background kick must never clear or
 * submit what the user is typing.
 */
export async function kickContinuation(
  getQueueForChat: (chatId: string) => ReadonlyArray<unknown>,
  submitMessage: SubmitMessage,
  conversationId: string,
): Promise<void> {
  if (getQueueForChat(conversationId).length > 0) return
  await submitMessage(null, { conversationId, preserveDraft: true })
}

export function createSubmitFlow(deps: SubmitFlowDeps): SubmitMessage {
  return async function submitMessage(input, options) {
    let chatInput = reconcileChatInput(input, deps.live.getCurrentChatInput())
    const state: SubmissionState = { streamingChatId: undefined, ownedTurnAtSettle: true, presentation: null }

    const runSubmission = async (existingConversationId?: string) => {
      const draft = prepareTurnDraft(deps, chatInput, options, existingConversationId)
      chatInput = draft.chatInput

      // Kick off model refresh in parallel — don't block UI updates on it
      const modelRefreshPromise = deps.data.refreshDefaultModel()

      // Decide synchronously whether this turn lands in the dashed-bubble
      // queue panel rather than the transcript. Live reads — see deps note.
      const shouldQueue =
        draft.isExisting &&
        (deps.engine.streaming.isActive(draft.conversationId) || deps.queue.getForChat(draft.conversationId).length > 0)

      const turn = createTurnPresentation(shouldQueue, {
        conversationId: draft.conversationId,
        chatInput: draft.chatInput,
        initialMessage: draft.initialMessage,
        preserveDraft: !!draft.options?.preserveDraft,
        inputBeforeSubmit: draft.inputBeforeSubmit,
        queue: deps.queue,
        updateLastMessage: deps.store.updateLastMessage,
        setChatInput: deps.store.setChatInput,
        commit: () => {
          commitTurnStart(deps, draft)
          state.streamingChatId = draft.conversationId
        },
      })
      // Queue cap race lost — the draft is already restored, drop the submission.
      if (!turn) return
      state.presentation = turn

      // Set the active chat after the initial hydration
      // For new chats this is a temp ID that will change w/ stream metadata
      if (!draft.options?.preserveActiveChat) {
        deps.store.setActiveChatId(draft.conversationId)
      }

      draft.options?.onSubmit?.(draft.conversationId)

      const autoTools = deps.actions.createAutoToolTracker({
        getConversationId: () => draft.conversationId,
        startFollowUp: () => kickContinuation(deps.queue.getForChat, submitMessage, draft.conversationId),
      })

      const { submittedAttachments, submittedAttachedContext } = await collectSubmissionContext(deps, draft, turn)
      const backendPlugins = await resolveModelAndPlugins(deps, draft, modelRefreshPromise)
      const resolved = await resolveTurnAttachments(deps, draft, turn, submittedAttachedContext)
      if (!resolved) return

      const chatRequestBody = buildChatRequestBody({
        chatInput: draft.chatInput,
        conversationId: draft.isExisting ? draft.conversationId : draft.preparedConversationId,
        opensNewConversation: draft.preparedConversationId !== undefined,
        activeShortcut: draft.activeShortcut,
        activeSkills: draft.activeSkills,
        attachedContext: resolved.attachedContext,
        modelId: draft.modelId,
        backendPlugins,
        useMemory: deps.snapshot.autoMemoryCollection,
        reasoningEffort: deps.snapshot.reasoningEffort,
        options: draft.options,
        msgId: draft.msgId,
      })

      trackSubmitMessage({
        chatInput: draft.chatInput,
        options: draft.options,
        mentions: draft.mentions,
        attachedContext: resolved.attachedContext,
        submittedAttachmentsCount: submittedAttachments.length,
        isNewChat: !draft.isExisting,
      })

      await runEngineTurn(deps, draft, turn, state, {
        chatRequestBody,
        attachedContext: resolved.attachedContext,
        includedAttachments: resolved.includedAttachments,
        autoTools,
        onTurnAccepted: resolved.onTurnAccepted,
      })
    }

    try {
      await runSubmission(
        options?.conversationId ?? (deps.snapshot.isExistingChat ? deps.snapshot.activeChatId : undefined),
      )
    } catch (err) {
      // Prefer the id of the conversation that was actually streaming —
      // `activeChatId` is captured at render time and is stale for new chats
      // (temp id → real id) or when the user switched chats mid-stream.
      const chatId = state.streamingChatId ?? deps.snapshot.activeChatId ?? ''
      const failure = toChatTurnError(err, { phase: 'submit', chatId })
      reportTurnError(failure, {
        isNewChat: !deps.snapshot.isExistingChat,
        messageLength: chatInput?.length,
        hasAttachments: deps.snapshot.attachments.length > 0,
      })
      handleTurnFailure(deps, failure, {
        chatId,
        wasTurnCommitted: state.presentation?.committed ?? false,
        // A promptless continuation commits no user bubble — the role-fallback
        // stamp would land on the previous turn's user message.
        userStamp: { omit: !chatInput },
      })
    } finally {
      // A superseded turn leaves the shared per-chat state to its successor.
      if (state.streamingChatId && state.ownedTurnAtSettle) {
        deps.engine.streaming.end(state.streamingChatId)
      }
      // Roll back whatever the presentation never committed (queue bubble
      // after a cancel or network drop).
      state.presentation?.onSettled()
      // Refresh history to pick up title after stream completes
      deps.data.invalidateHistoryPreview()
    }
  }
}
